import { useState, useRef, useEffect } from 'react';
import { Formik, Form, Field as FormikField } from 'formik';
import * as Yup from 'yup';
import { TextField } from 'formik-mui';
import FormHelperText from '@mui/material/FormHelperText';
import moment from 'moment';
import { Button, Dropdown, FadeInAnimation } from '../shared';

import { contactUsDomain } from '../../domain'

import useResponsive from '../../../utils/responsive';

const corporateBookingsInitialValues = {
    title: '',
    nameAndSurname: '',
    emailAddress: '',
    contactNumber: '',
    company: '',
    numberOfGuests: '',
    startDate: moment().add(1, 'day').format('YYYY-MM-DD'),
    endDate: moment().add(2, 'day').format('YYYY-MM-DD'),
    city: '',
};
const corporateBookingsValidationSchema = Yup.object({
    title: Yup.string('').required('Title is required'),
    nameAndSurname: Yup.string('').required('Name and surname is required'),
    emailAddress: Yup.string('').email('Enter a valid email').required('Email is required'),
    contactNumber: Yup.string('')
        .matches(/^[0-9,+]*$/, 'Phone number is invalid')
        .min(9, 'Phone number is too short (minimum 9)')
        .max(15, 'Phone number is too long (maximum 15)')
        .required('Phone number is required'),
    numberOfGuests: Yup.string('')
        .matches(
            /^(0*[1-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/,
            'Number of guests must be more than 0',
        )
        .required('Number of guests is required'),
    startDate: Yup.date().default(() => moment().format('YYYY-MM-DD')),
    endDate: Yup.date()
        .default(() => moment().format('YYYY-MM-DD'))
        .min(Yup.ref('startDate'), "End Date can't be before Start Date"),
});

const titles = [
    {
        label: 'Mr',
        value: 'Mr',
        id: 'mr-select',
    },
    {
        label: 'Mrs',
        value: 'Mrs',
        id: 'mrs-select',
    },
    {
        label: 'Ms',
        value: 'Ms',
        id: 'ms-select',
    },
];

const CorporateBookingsForm = () => {
    const [titleType, setTitleType] = useState(null);
    const [contactUsSubmitError, setContactUsSubmitError] = useState(null);
    const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
    const { isMobile, isTablet } = useResponsive();

    const startDateRef = useRef('startDate');
    const myRef = useRef(null);

    useEffect(() => {
        if (successfullySubmitted && myRef.current) {
            myRef.current.scrollIntoView();
        }
    }, [successfullySubmitted]);    

    const onSubmit = async ({
        title,
        nameAndSurname,
        emailAddress,
        contactNumber,
        company,
        numberOfGuests,
        startDate,
        endDate,
        city,
    }) => {
        try {
            await contactUsDomain.submitCorporateBookings(
                title,
                nameAndSurname,
                emailAddress,
                contactNumber,
                company,
                numberOfGuests,
                startDate,
                endDate,
                city,
            );

            setContactUsSubmitError(null);
            setSuccessfullySubmitted(true);
            // const element = document.getElementById('message');
            // element.scrollIntoView();
        } catch (error) {
            setContactUsSubmitError(error.message);
            setSuccessfullySubmitted(false);
        }
    };

    const onTitleChanged = (_title, event, formikOnChange) => {
        setTitleType(_title);
        formikOnChange(event);
    };

    const ThankYouMessage = () => {
        if (!successfullySubmitted) {
            return null;
        }

        return (
            <div ref={myRef} className='get-in-touch-form-thank-you-container get-in-touch-form-thank-you-height-50' id="message">
                <h3>Thanks for Getting in Touch!</h3>
                <p>Someone from HOMii will be in touch ASAP</p>
            </div>
        );
    };

    const renderDesktopForm = () => {
        if (successfullySubmitted || isMobile || isTablet) {
            return null;
        }
        return (
            <div className='corporate-bookings-form-container'>
                <div>
                    <Formik
                        initialValues={corporateBookingsInitialValues}
                        validationSchema={corporateBookingsValidationSchema}
                        onSubmit={onSubmit}
                        validateOnBlur
                        validateOnChange={false}
                    >
                        {({
                            submitForm,
                            isSubmitting,
                            touched,
                            errors,
                            handleChange,
                            handleBlur,
                        }) => (
                            <Form>

                                <div className='corporate-bookings-input-container'>

                                    <div className='corporate-bookings-drop-down-container'>

                                        <Dropdown
                                            placeholder="Title*"
                                            selectId="title_dropdown"
                                            options={titles}
                                            onChange={(_title, event) => {
                                                onTitleChanged(_title, event, handleChange);
                                            }}
                                            name="title"
                                            defaultValue={titleType}
                                            helperText={touched.title ? errors.title : ''}
                                            error={touched.title && !!errors.title}
                                            handleBlur={handleBlur}
                                        />

                                    </div>

                                    <FormikField className='corporate-bookings-input-right'
                                        component={TextField}
                                        required
                                        name="nameAndSurname"
                                        id="nameAndSurname"
                                        type="text"
                                        label="Name and Surname"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={
                                            touched.nameAndSurname ? errors.nameAndSurname : ''
                                        }
                                        error={touched.nameAndSurname && !!errors.nameAndSurname}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />
                                    
                                </div>

                                <div className='corporate-bookings-input-container'>

                                    <FormikField className='corporate-bookings-input-left'
                                        component={TextField}
                                        required
                                        name="contactNumber"
                                        id="contactNumber"
                                        type="tel"
                                        label="Contact Number"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={
                                            touched.contactNumber ? errors.contactNumber : ''
                                        }
                                        error={touched.contactNumber && !!errors.contactNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <FormikField className='corporate-bookings-input-right'
                                        component={TextField}
                                        name="company"
                                        id="company"
                                        type="text"
                                        label="Company"
                                        variant="outlined"
                                        color="secondary"
                                        placeholder="Company"
                                        helperText={touched.company ? errors.company : ''}
                                        error={touched.company && !!errors.company}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                </div>

                               <div className='corporate-bookings-input-container'>

                                    <FormikField className='corporate-bookings-input-left'
                                        component={TextField}
                                        required
                                        name="emailAddress"
                                        id="emailAddress"
                                        type="email"
                                        label="Email Address"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={touched.emailAddress ? errors.emailAddress : ''}
                                        error={touched.emailAddress && !!errors.emailAddress}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <div className='corporate-bookings-form-dates-container'>

                                        <FormikField
                                            ref={startDateRef}
                                            component={TextField}
                                            required
                                            name="startDate"
                                            id="startDate"
                                            label="Start Date"
                                            variant="outlined"
                                            type="date"
                                            color="secondary"
                                            InputProps={{
                                                inputProps: {
                                                    min: corporateBookingsInitialValues.startDate,
                                                },
                                            }}
                                            helperText={touched.startDate ? errors.startDate : ''}
                                            error={touched.startDate && !!errors.startDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                        />

                                        <FormikField className='corporate-bookings-end-date'
                                            component={TextField}
                                            required
                                            name="endDate"
                                            id="endDate"
                                            label="End Date"
                                            variant="outlined"
                                            type="date"
                                            color="secondary"
                                            helperText={touched.endDate ? errors.endDate : ''}
                                            error={touched.endDate && !!errors.endDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                        />

                                    </div>

                                </div>

                                <div className='corporate-bookings-input-container'>

                                    <FormikField className='corporate-bookings-input-left'
                                        component={TextField}
                                        required
                                        name="numberOfGuests"
                                        id="numberOfGuests"
                                        type="number"
                                        label="Number of Guests"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={
                                            touched.numberOfGuests ? errors.numberOfGuests : ''
                                        }
                                        error={touched.numberOfGuests && !!errors.numberOfGuests}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                        InputProps={{ inputProps: { min: 1 } }}
                                    />

                                    <FormikField className='corporate-bookings-input-right'
                                        component={TextField}
                                        name="city"
                                        id="city"
                                        type="text"
                                        label="City"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={touched.city ? errors.city : ''}
                                        error={touched.city && !!errors.city}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                </div>

                                <FormHelperText error>{contactUsSubmitError}</FormHelperText>

                                <FadeInAnimation>
                                    <div className='get-in-touch-form-button-container'>
                                        <Button
                                            style={{ marginTop: '26px' }}
                                            variant="contained"
                                            color="primary"
                                            isLoading={isSubmitting}
                                            onClick={submitForm}
                                        >
                                            Submit
                                        </Button>
                                    </div>
                                </FadeInAnimation>

                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        );
    };

    const renderMobileAndTablet = () => {
        if ((isMobile || isTablet) && !successfullySubmitted) {
            return (
                <div className='corporate-bookings-form-container'>
                    <div>
                        <Formik
                            initialValues={corporateBookingsInitialValues}
                            validationSchema={corporateBookingsValidationSchema}
                            onSubmit={onSubmit}
                            validateOnBlur
                            validateOnChange={false}
                        >
                            {({
                                submitForm,
                                isSubmitting,
                                touched,
                                errors,
                                handleChange,
                                handleBlur,
                            }) => (

                                <Form>

                                    <div className='get-in-touch-form-dropdown-container-bottom'>

                                        <Dropdown
                                            placeholder="Title*"
                                            selectId="title_dropdown"
                                            options={titles}
                                            onChange={(_title, event) => {
                                                onTitleChanged(_title, event, handleChange);
                                            }}
                                            name="title"
                                            defaultValue={titleType}
                                            helperText={touched.title ? errors.title : ''}
                                            error={touched.title && !!errors.title}
                                            handleBlur={handleBlur}
                                        />

                                    </div>

                                    <FormikField className='get-in-touch-form-field'
                                        component={TextField}
                                        required
                                        name="nameAndSurname"
                                        id="nameAndSurname"
                                        type="text"
                                        label="Name and Surname"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={
                                            touched.nameAndSurname ? errors.nameAndSurname : ''
                                        }
                                        error={touched.nameAndSurname && !!errors.nameAndSurname}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <FormikField className='get-in-touch-form-field'
                                        component={TextField}
                                        required
                                        name="contactNumber"
                                        id="contactNumber"
                                        type="tel"
                                        label="Contact Number"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={
                                            touched.contactNumber ? errors.contactNumber : ''
                                        }
                                        error={touched.contactNumber && !!errors.contactNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <FormikField className='get-in-touch-form-field'
                                        component={TextField}
                                        name="company"
                                        id="company"
                                        type="text"
                                        label="Company"
                                        variant="outlined"
                                        color="secondary"
                                        placeholder="Company"
                                        helperText={touched.company ? errors.company : ''}
                                        error={touched.company && !!errors.company}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <FormikField className='get-in-touch-form-field'
                                        component={TextField}
                                        required
                                        name="emailAddress"
                                        id="emailAddress"
                                        type="email"
                                        label="Email Address"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={touched.emailAddress ? errors.emailAddress : ''}
                                        error={touched.emailAddress && !!errors.emailAddress}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <FormikField className='get-in-touch-form-field'
                                        component={TextField}
                                        required
                                        name="startDate"
                                        id="startDate"
                                        label="Start Date"
                                        variant="outlined"
                                        type="date"
                                        color="secondary"
                                        InputProps={{
                                            inputProps: {
                                                min: corporateBookingsInitialValues.startDate,
                                            },
                                        }}
                                        helperText={touched.startDate ? errors.startDate : ''}
                                        error={touched.startDate && !!errors.startDate}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <FormikField className='get-in-touch-form-field get-in-touch-form-field-right'
                                        component={TextField}
                                        required
                                        name="endDate"
                                        id="endDate"
                                        label="End Date"
                                        variant="outlined"
                                        type="date"
                                        color="secondary"
                                        min={moment().format('YYYY-MM-DD')}
                                        helperText={touched.endDate ? errors.endDate : ''}
                                        error={touched.endDate && !!errors.endDate}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <FormikField className='get-in-touch-form-field'
                                        component={TextField}
                                        required
                                        name="numberOfGuests"
                                        id="numberOfGuests"
                                        type="number"
                                        label="Number of Guests"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={
                                            touched.numberOfGuests ? errors.numberOfGuests : ''
                                        }
                                        error={touched.numberOfGuests && !!errors.numberOfGuests}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                        min="0"
                                    />

                                    <FormikField className='get-in-touch-form-field'
                                        component={TextField}
                                        name="city"
                                        id="city"
                                        type="text"
                                        label="City"
                                        variant="outlined"
                                        color="secondary"
                                        helperText={touched.city ? errors.city : ''}
                                        error={touched.city && !!errors.city}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />

                                    <FormHelperText error>{contactUsSubmitError}</FormHelperText>

                                    <FadeInAnimation>
                                        <Button
                                            style={{ marginTop: '26px' }}
                                            variant="contained"
                                            color="primary"
                                            isLoading={isSubmitting}
                                            onClick={submitForm}
                                        >
                                            Submit
                                        </Button>
                                    </FadeInAnimation>

                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className='container-root container-root-maxWidthLg' style={{ marginTop: isMobile ? 65 : 124 }}>
            <>
                {!successfullySubmitted && (
                    <FadeInAnimation>
                        <div className='get-in-touch-form-header'>
                            <h3>Get in Touch</h3>
                            <p className='get-in-touch-form-paragraph'>
                                Get in touch and a consultant will help you secure your booking.
                            </p>
                        </div>
                    </FadeInAnimation>
                )}
                {renderDesktopForm()}
                {renderMobileAndTablet()}
            </>

            <ThankYouMessage />
        </div>
    );
};

export default CorporateBookingsForm;

